// src/hq/roomVideos.js
//
// One source of truth for the 12 HQ room clips. Each entry maps a room
// key to its cinematic video (served from Higgsfield's CloudFront for
// now — same-origin migration to Vercel Blob is a follow-up), the
// resident AI, its live-data quote source, and the widget slot the
// Command Center grid renders inside the card.
//
// Every video is a Kling 3.0 Turbo render, 1080p, 16:9, 8 seconds,
// generated 2026-07-17 to the HQ visual doctrine (deep concrete +
// glass + marble, amber #F5A623 accent only, half-human half-robot
// cyborg employees in sharp black tailored suits with amber joint
// trace). See docs/hq/command_center_grid_reference.md.
//
// Rooms whose resident AI isn't wired to a live signal yet (LEDGER,
// ASCEND) are marked `stub: true` — their card labels them
// NOT YET ACTIVE per the constitution's honesty rule.

export const ROOM_VIDEOS = {
  home: {
    number: 1,
    title: 'HOME (COMMAND CENTER)',
    tagline: 'Your headquarters. Live overview.',
    resident: { name: 'PILOT', role: 'Onboarding & Guide AI' },
    videoUrl: 'https://d8j0ntlcm91z4.cloudfront.net/user_3DxmgQSSiGJ1JcYcN2Uh9WfUPDA/hf_20260717_235519_952807ec-76aa-4b28-9e64-d363c866ffc5.mp4',
    tab: 'command_center',
  },
  leads: {
    number: 2,
    title: 'LEADS ROOM',
    tagline: 'Find. Score. Prioritize.',
    resident: { name: 'SCOUT', role: 'Lead Intelligence AI' },
    videoUrl: 'https://d8j0ntlcm91z4.cloudfront.net/user_3DxmgQSSiGJ1JcYcN2Uh9WfUPDA/hf_20260717_235537_b885c9c5-2dac-4026-a826-695e211b2abf.mp4',
    tab: 'leads_intelligence',
  },
  pipeline: {
    number: 3,
    title: 'PIPELINE ROOM',
    tagline: 'Manage your sales pipeline.',
    resident: { name: 'UPLIFT', role: 'Sales Coach AI' },
    videoUrl: 'https://d8j0ntlcm91z4.cloudfront.net/user_3DxmgQSSiGJ1JcYcN2Uh9WfUPDA/hf_20260718_002258_6c6c2975-4b3f-443a-8381-cb6010d5fbc2.mp4',
    tab: 'kanban',
  },
  calls: {
    number: 4,
    title: 'CALLS ROOM',
    tagline: 'Power dialer. Live calls. AI coaching.',
    resident: { name: 'NOVA', role: 'Communications AI' },
    videoUrl: 'https://d8j0ntlcm91z4.cloudfront.net/user_3DxmgQSSiGJ1JcYcN2Uh9WfUPDA/hf_20260718_002408_b7c41ff6-d0c0-44dd-8251-957d15cebb98.mp4',
    tab: 'call_queue',
  },
  messages: {
    number: 5,
    title: 'MESSAGES ROOM',
    tagline: 'All SMS, email, and follow-ups.',
    resident: { name: 'NUDGE', role: 'Follow-Up AI' },
    videoUrl: 'https://d8j0ntlcm91z4.cloudfront.net/user_3DxmgQSSiGJ1JcYcN2Uh9WfUPDA/hf_20260718_002308_bbd73c59-2a8d-4713-b249-6e77c2429d99.mp4',
    tab: 'messages_intelligence',
  },
  clients: {
    number: 6,
    title: 'CLIENTS ROOM',
    tagline: '360° client view. Relationships.',
    resident: { name: 'RETAIN', role: 'Client Success AI' },
    videoUrl: 'https://d8j0ntlcm91z4.cloudfront.net/user_3DxmgQSSiGJ1JcYcN2Uh9WfUPDA/hf_20260718_002316_741fab13-625f-4488-864c-8dae03e8947b.mp4',
    tab: 'client_intelligence',
  },
  commissions: {
    number: 7,
    title: 'COMMISSIONS ROOM',
    tagline: 'Track commissions, overrides, chargebacks.',
    resident: { name: 'LEDGER', role: 'Finance AI', stub: true },
    videoUrl: 'https://d8j0ntlcm91z4.cloudfront.net/user_3DxmgQSSiGJ1JcYcN2Uh9WfUPDA/hf_20260717_235545_19e9af1b-b33e-43cf-a458-bd6618b2cef0.mp4',
    tab: 'revenue_intelligence',
  },
  calendar: {
    number: 8,
    title: 'CALENDAR ROOM',
    tagline: 'Appointments. Reminders. Schedule.',
    resident: { name: 'NOVA', role: 'Books from conversations' },
    videoUrl: 'https://d8j0ntlcm91z4.cloudfront.net/user_3DxmgQSSiGJ1JcYcN2Uh9WfUPDA/hf_20260718_002330_b199d9c6-de2a-4ff4-92ca-01c9ed26c39d.mp4',
    tab: 'calendar',
  },
  reports: {
    number: 9,
    title: 'REPORTS ROOM',
    tagline: 'Real data. Real growth.',
    resident: { name: 'ASCEND', role: 'Analytics AI', stub: true },
    videoUrl: 'https://d8j0ntlcm91z4.cloudfront.net/user_3DxmgQSSiGJ1JcYcN2Uh9WfUPDA/hf_20260718_002339_d1a407f7-2e3c-437c-8c2d-d226967e2d23.mp4',
    tab: 'pnl',
  },
  workforce: {
    number: 10,
    title: 'AI WORKFORCE ROOM',
    tagline: "Meet your AI team. See what they're doing.",
    resident: { name: 'PILOT', role: 'Your guide to the roster' },
    videoUrl: 'https://d8j0ntlcm91z4.cloudfront.net/user_3DxmgQSSiGJ1JcYcN2Uh9WfUPDA/hf_20260717_235529_165169fd-6aff-48e6-aebf-c22fe924e9cf.mp4',
    tab: 'operations_floor',
  },
  compliance: {
    number: 11,
    title: 'COMPLIANCE ROOM',
    tagline: 'Stay compliant. Every send gated.',
    resident: { name: 'SHIELD', role: 'Compliance AI' },
    videoUrl: 'https://d8j0ntlcm91z4.cloudfront.net/user_3DxmgQSSiGJ1JcYcN2Uh9WfUPDA/hf_20260718_002347_a683539f-fdad-47f5-979f-a1fdbd3c24cd.mp4',
    tab: 'compliance_intelligence',
  },
  settings: {
    number: 12,
    title: 'SETTINGS ROOM',
    tagline: 'Integrations, users, phone, and more.',
    resident: { name: 'SENTRY', role: 'Platform Watch AI' },
    videoUrl: 'https://d8j0ntlcm91z4.cloudfront.net/user_3DxmgQSSiGJ1JcYcN2Uh9WfUPDA/hf_20260718_002358_adc3a9d0-27c5-4514-8348-a0b108704649.mp4',
    tab: 'settings',
  },
};

// Reverse map: tab → room key, so <RoomEntrance> can look up the video
// when a tab change fires from anywhere (grid card, sidebar, deep link).
export const TAB_TO_ROOM = Object.fromEntries(
  Object.entries(ROOM_VIDEOS).map(([key, meta]) => [meta.tab, key])
);

// Ordered list, useful for the 4x3 grid layout.
export const ROOMS_ORDERED = Object.entries(ROOM_VIDEOS).map(([key, meta]) => ({ key, ...meta }));
